import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '../../components/feature/SEOHead';

const portalSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Owner Portal – True North Vacations',
  description: 'Access your True North Vacations owner portal to track bookings, revenue, maintenance logs, and financial reports for your Port Severn waterfront property.',
  url: `${import.meta.env.VITE_SITE_URL || ''}/owners-portal`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'True North Vacations',
    url: `${import.meta.env.VITE_SITE_URL || ''}`,
  },
};

export default function OwnersPortalPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Owner Portal | True North Vacations Property Management Dashboard"
        description="Access your True North Vacations owner portal. Track bookings, revenue, maintenance logs, and financial reports for your Port Severn waterfront property in real time."
        keywords="owner portal vacation rental, property management dashboard Port Severn, waterfront rental owner access, True North Vacations portal"
        canonical={`${import.meta.env.VITE_SITE_URL || ''}/owners-portal`}
        ogTitle="Owner Portal | True North Vacations Property Management Dashboard"
        ogDescription="Access your True North Vacations owner portal to track bookings, revenue, and financials for your Port Severn waterfront property."
        schemaJson={portalSchema}
      />
      {/* Navigation */}
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-compass-3-line text-2xl text-[#0A2540]"></i>
              <span className="text-base sm:text-xl font-serif font-semibold text-[#0A2540]">True North Vacations</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <Link to="/listings" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Properties</Link>
              <Link to="/why-list" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">For Owners</Link>
              <Link to="/services" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Services</Link>
              <Link to="/owners-portal" className="text-sm font-light text-[#C9A961] whitespace-nowrap">Owner Portal</Link>
              <Link to="/contact" className="text-sm font-light text-gray-700 hover:text-[#C9A961] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/contact" className="bg-[#C9A961] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors whitespace-nowrap">List Your Property</Link>
            </div>
            <button className="lg:hidden p-2 text-[#0A2540] cursor-pointer" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
              <i className={`text-2xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
          {menuOpen && (
            <div className="lg:hidden pt-4 pb-4 border-t border-gray-100 mt-4 space-y-3">
              <Link to="/listings" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Properties</Link>
              <Link to="/why-list" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">For Owners</Link>
              <Link to="/services" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Services</Link>
              <Link to="/owners-portal" onClick={() => setMenuOpen(false)} className="block text-sm text-[#C9A961] py-2">Owner Portal</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="block text-sm text-gray-700 hover:text-[#C9A961] py-2">Contact</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)} className="inline-block bg-[#C9A961] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#B8984E] transition-colors mt-2">List Your Property</Link>
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[280px] sm:h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/568982d3988647d98d9ff90c3539779d.jpg" alt="Owner Portal" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-5xl font-serif text-white mb-3 sm:mb-4">Owner Portal</h1>
          <p className="text-base sm:text-xl text-white/90 font-light">Access your property management dashboard</p>
        </div>
      </section>

      {/* Portal Access */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 lg:p-12">
            <div className="text-center mb-8 sm:mb-12">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#0A2540] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-keyhole-line text-3xl sm:text-4xl text-[#C9A961]"></i>
              </div>
              <h2 className="text-2xl sm:text-4xl font-bold text-[#0A2540] mb-4">Secure Portal Access</h2>
              <p className="text-gray-600 text-base sm:text-lg">Our owner portal is powered by industry-leading property management software, providing you with real-time access to your property's performance, bookings, and financials.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
              {[
                { icon: 'ri-line-chart-line', title: 'Real-Time Analytics', description: 'Track bookings, revenue, and occupancy rates instantly' },
                { icon: 'ri-calendar-check-line', title: 'Booking Calendar', description: 'View reservations and block personal dates' },
                { icon: 'ri-file-text-line', title: 'Financial Reports', description: 'Access detailed statements and tax documents' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 bg-[#F5F1E8] rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-2xl sm:text-3xl text-[#C9A961]`}></i>
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-[#0A2540] mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#F5F1E8] rounded-2xl p-6 sm:p-8 mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-[#0A2540] mb-4 text-center">Access Your Dashboard</h3>
              <p className="text-gray-700 text-center mb-6 text-sm sm:text-base">We partner with leading property management platforms to provide you with comprehensive access to your property's data.</p>
              <div className="text-center">
                <a href="https://www.hostfully.com/property-management-platform/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 bg-[#0A2540] text-white px-6 sm:px-8 py-4 rounded-full text-base sm:text-lg hover:bg-[#0A2540]/90 transition-all whitespace-nowrap">
                  <i className="ri-external-link-line"></i>
                  <span>Access Property Management Portal</span>
                </a>
              </div>
            </div>

            <div className="border-t pt-8">
              <h3 className="text-lg sm:text-xl font-bold text-[#0A2540] mb-4">Need Help?</h3>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">If you're having trouble accessing your portal or need assistance, our team is here to help.</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center space-x-2 border-2 border-[#0A2540] text-[#0A2540] px-6 py-3 rounded-full hover:bg-[#0A2540] hover:text-white transition-all whitespace-nowrap">
                  <i className="ri-mail-line"></i>
                  <span>Contact Support</span>
                </Link>
                <a href="tel:+17055382000" className="inline-flex items-center justify-center space-x-2 border-2 border-[#0A2540] text-[#0A2540] px-6 py-3 rounded-full hover:bg-[#0A2540] hover:text-white transition-all whitespace-nowrap">
                  <i className="ri-phone-line"></i>
                  <span>Call Us: (705) 538-2000</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4">Portal Features</h2>
            <p className="text-gray-600 text-base sm:text-lg">Everything you need to manage your investment</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
            {[
              { icon: 'ri-money-dollar-circle-line', title: 'Revenue Tracking', description: 'Monitor earnings and payment schedules' },
              { icon: 'ri-calendar-event-line', title: 'Reservation Management', description: 'View and manage all bookings' },
              { icon: 'ri-image-line', title: 'Property Photos', description: 'Access professional listing images' },
              { icon: 'ri-tools-line', title: 'Maintenance Logs', description: 'Track all property maintenance' },
              { icon: 'ri-star-line', title: 'Guest Reviews', description: 'Read feedback from guests' },
              { icon: 'ri-bar-chart-box-line', title: 'Performance Metrics', description: 'Analyze occupancy and trends' },
              { icon: 'ri-file-download-line', title: 'Document Center', description: 'Download contracts and reports' },
              { icon: 'ri-message-3-line', title: 'Direct Messaging', description: 'Communicate with our team' }
            ].map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#C9A961]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                  <i className={`${feature.icon} text-2xl sm:text-3xl text-[#C9A961]`}></i>
                </div>
                <h3 className="text-sm sm:text-lg font-bold text-[#0A2540] mb-1 sm:mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Not an Owner Yet?</h2>
          <p className="text-base sm:text-xl text-gray-300 mb-8 leading-relaxed">Join True North Vacations and gain access to our comprehensive property management platform.</p>
          <Link to="/contact" className="inline-flex items-center space-x-2 bg-[#C9A961] text-white px-8 py-4 rounded-full text-lg hover:bg-[#B8984E] transition-all whitespace-nowrap">
            <span>List Your Property Today</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-12">
            <div className="col-span-2 lg:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <i className="ri-compass-3-line text-2xl text-[#C9A961]"></i>
                <span className="text-lg font-serif font-semibold">True North Vacations</span>
              </div>
              <p className="text-gray-400 text-sm">Port Severn's Premier Luxury Vacation Rental Management</p>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/services" className="hover:text-white transition-colors">Property Management</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Maintenance</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Guest Services</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">For Owners</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/contact" className="hover:text-white transition-colors">List Your Property</Link></li>
                <li><Link to="/owners-portal" className="hover:text-white transition-colors">Owner Portal</Link></li>
                <li><Link to="/why-list" className="hover:text-white transition-colors">Revenue Calculator</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-[#C9A961]">Properties</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/listings" className="hover:text-white transition-colors">Current Listings</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Booking Inquiry</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2025 True North Vacations. All rights reserved.</p>
            <div className="flex items-center space-x-5">
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-facebook-fill text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-instagram-line text-xl"></i></a>
              <a href="#" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-linkedin-fill text-xl"></i></a>
              <a href="mailto:info@truenorthvacations.com" className="text-[#C9A961] hover:text-white transition-colors"><i className="ri-mail-line text-xl"></i></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
